import { DT, GRAVITY } from "../constants.ts";
import { add, dist, flatBasis, norm, rng, scale, sub, type Vec3, viewDir } from "../math.ts";
import { Btn, type InputCmd } from "../net/input.ts";
import { abilIdx, ActionKind, hv, MoveKind, setHv } from "../sim/components.ts";
import {
  aim,
  type HitscanOpts,
  hitscan,
  type HitMode,
  type HitResult,
  applyDamage,
  applyImpulse,
  applyStatus,
  applyHeal,
  coneConstructs,
  coneTargets,
  isEnemy,
  moveProjectile,
  type ProjectileHit,
  playersInRadius,
  spawnProjectile,
  type StatusKind,
} from "../sim/combat.ts";
import { CONSTRUCT_FLAG_BLOCKS, damageConstruct } from "../sim/constructs.ts";
import { releaseHeldTarget } from "../sim/held.ts";
export { grab, release } from "../sim/held.ts";
import type { MoveMods } from "../sim/movement.ts";
import type { Sim } from "../sim/sim.ts";
import { HeroId, heldWeapon, Slot, WEAPON_SWAP_SECONDS } from "./types.ts";

// ---------------------------------------------------------------- shared helpers

export const secs = (s: number) => Math.round(s * 60);

export function ready(sim: Sim, eid: number, slot: Slot) {
  return sim.c.Abil.charges[abilIdx(eid, slot)]! > 0;
}

export function consume(sim: Sim, eid: number, slot: Slot) {
  const c = sim.c;
  const i = abilIdx(eid, slot);
  c.Abil.charges[i] = c.Abil.charges[i]! - 1;
  if (c.Abil.cd[i]! <= 0) c.Abil.cd[i] = secs(sim.hero(eid).abilities[slot]?.cooldown ?? 0);
  sim.emit({ type: "cast", owner: c.Net.id[eid]!, slot, hero: c.Player.hero[eid]! });
}

export function useUlt(sim: Sim, eid: number, input: InputCmd, pressed: number): boolean {
  const c = sim.c;
  if (!(pressed & Btn.Ultimate) || c.Ult.charge[eid]! < 1) return false;
  c.Ult.charge[eid] = 0;
  action(sim, eid, ActionKind.Ult, 30);
  sim.emit({ type: "cast", owner: c.Net.id[eid]!, slot: Slot.Ultimate, hero: c.Player.hero[eid]! });
  void input;
  return true;
}

export function action(sim: Sim, eid: number, kind: ActionKind, ticks: number) {
  sim.c.Weapon.action[eid] = kind;
  sim.c.Weapon.actionTicks[eid] = ticks;
}

/** Common fire gate: cooldown, ammo and reload handling. Returns true if a shot may be fired now. */
export function trigger(sim: Sim, eid: number, held: boolean, infiniteAmmo = false): boolean {
  const c = sim.c;
  const def = sim.hero(eid);
  if (!held || c.Weapon.fireCd[eid]! > 0 || c.Weapon.reload[eid]! > 0) return false;
  if (heldWeapon(def, c.Weapon.slot[eid]!).ammo > 0 && !infiniteAmmo) {
    if (c.Weapon.ammo[eid]! <= 0) {
      startReload(sim, eid);
      return false;
    }
    c.Weapon.ammo[eid] = c.Weapon.ammo[eid]! - 1;
  }
  c.Weapon.shots[eid] = (c.Weapon.shots[eid]! + 1) & 0xffff;
  return true;
}

export function startReload(sim: Sim, eid: number) {
  const c = sim.c;
  const def = sim.hero(eid);
  const w = heldWeapon(def, c.Weapon.slot[eid]!);
  if (w.ammo <= 0 || c.Weapon.reload[eid]! > 0 || c.Weapon.ammo[eid]! >= w.ammo) return;
  c.Weapon.reload[eid] = secs(w.reloadTime);
  action(sim, eid, ActionKind.Reload, secs(w.reloadTime));
}

/** Puts weapon `slot` in hand (heroes with `def.weapons`): magazines swap, a reload in progress stops. */
export function switchWeapon(sim: Sim, eid: number, slot: number) {
  const c = sim.c;
  if (!sim.hero(eid).weapons || slot === c.Weapon.slot[eid] || slot < 0 || slot > 1) return;
  const held = c.Weapon.ammo[eid]!;
  c.Weapon.ammo[eid] = c.Weapon.stowed[eid]!;
  c.Weapon.stowed[eid] = held;
  c.Weapon.slot[eid] = slot;
  c.Weapon.reload[eid] = 0;
  c.Weapon.fireCd[eid] = Math.max(c.Weapon.fireCd[eid]!, secs(WEAPON_SWAP_SECONDS));
  action(sim, eid, ActionKind.Reload, secs(WEAPON_SWAP_SECONDS));
}

export function commonInput(sim: Sim, eid: number, input: InputCmd, pressed: number) {
  const c = sim.c;
  if (sim.hero(eid).weapons) {
    const cur = c.Weapon.slot[eid]!;
    if (pressed & Btn.Weapon1) switchWeapon(sim, eid, 0);
    else if (pressed & Btn.Weapon2) switchWeapon(sim, eid, 1);
    else if (pressed & Btn.WeaponNext) switchWeapon(sim, eid, 1 - cur);
  }
  if (pressed & Btn.Reload) startReload(sim, eid);
  if (pressed & Btn.Melee && c.Weapon.fireCd[eid]! <= 0) {
    c.Weapon.fireCd[eid] = secs(0.8);
    action(sim, eid, ActionKind.Melee, 20);
    for (const t of coneTargets(sim, eid, 2.6, 0.7, (e) => isEnemy(sim, eid, e))) {
      applyDamage(sim, t, 30, eid, { info: { kind: "melee" } });
      const d = viewDir(input.yaw, 0);
      applyImpulse(sim, t, { x: d.x * 4, y: 1.5, z: d.z * 4 });
    }
    for (const { eid: t } of coneConstructs(sim, eid, 2.6, 0.7)) damageConstruct(sim, t, 30, eid);
  }
}

/** Deterministic spread so the shooter's predicted tracers match the server. */
export function spreadDir(dir: Vec3, spread: number, seed: number): Vec3 {
  if (spread <= 0) return dir;
  const r = rng(seed);
  const a = r() * Math.PI * 2;
  const m = Math.sqrt(r()) * spread;
  const up = Math.abs(dir.y) > 0.95 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 1, z: 0 };
  const rt = norm({ x: dir.y * up.z - dir.z * up.y, y: dir.z * up.x - dir.x * up.z, z: dir.x * up.y - dir.y * up.x });
  const u = { x: rt.y * dir.z - rt.z * dir.y, y: rt.z * dir.x - rt.x * dir.z, z: rt.x * dir.y - rt.y * dir.x };
  return norm(add(dir, add(scale(rt, Math.cos(a) * m), scale(u, Math.sin(a) * m))));
}

/** Server-side fast-forward (ticks) for projectiles, capped at ~100ms. */
export function ffTicks(sim: Sim) {
  if (!sim.authority || sim.rewindTick === undefined) return 0;
  return Math.max(0, Math.min(6, Math.round(sim.tick - sim.rewindTick)));
}

export function launchProjectile(sim: Sim, eid: number, kind: number, speed: number, input: InputCmd, extra: { gravity?: number; radius?: number; seconds: number; target?: number; dir?: Vec3 }) {
  const a = aim(sim, eid);
  const dir = extra.dir ?? a.dir;
  const { right } = flatBasis(input.yaw);
  const origin = add(a.origin, add(scale(dir, 0.5), scale(right, 0.2)));
  const p = spawnProjectile(sim, eid, {
    kind,
    origin,
    vel: scale(dir, speed),
    gravity: extra.gravity,
    radius: extra.radius,
    seconds: extra.seconds,
    target: extra.target,
    seq: input.seq,
  });
  if (p !== undefined) {
    const n = ffTicks(sim);
    for (let i = 0; i < n && sim.eidOf(sim.c.Net.id[p]!) === p; i++) projectileRegistry[kind]?.tick(sim, p);
  }
}

export function wishDir(input: InputCmd): Vec3 {
  const { fwd, right } = flatBasis(input.yaw);
  let x = fwd.x * input.moveZ + right.x * input.moveX;
  let z = fwd.z * input.moveZ + right.z * input.moveX;
  const l = Math.hypot(x, z);
  if (l < 0.1) return fwd;
  x /= l;
  z /= l;
  return { x, y: 0, z };
}

export function startMove(sim: Sim, eid: number, kind: MoveKind, ticks: number, speed: number, v: Vec3, target = 0) {
  const c = sim.c;
  c.Move.kind[eid] = kind;
  c.Move.ticks[eid] = ticks;
  c.Move.speed[eid] = speed;
  c.Move.x[eid] = v.x;
  c.Move.y[eid] = v.y;
  c.Move.z[eid] = v.z;
  c.Move.target[eid] = target;
  c.Move.blocked[eid] = 0;
}

/** Sustained predicted flight: camera-relative WASD, Jump up, Crouch down, optional pitch climb. */
export function flight(sim: Sim, eid: number, input: InputCmd, mods: MoveMods, o: { speed: number; accel?: number; vertical?: number; pitchClimb?: boolean; fuelIdx?: number; drain?: number }): boolean {
  if (o.fuelIdx !== undefined) {
    const fuel = hv(sim.c, eid, o.fuelIdx);
    if (fuel <= 0) return false;
    setHv(sim.c, eid, o.fuelIdx, Math.max(0, fuel - (o.drain ?? 0) * DT));
  }
  const { fwd, right } = flatBasis(input.yaw);
  let vx = fwd.x * input.moveZ + right.x * input.moveX;
  let vz = fwd.z * input.moveZ + right.z * input.moveX;
  let vy = (input.buttons & Btn.Jump ? 1 : 0) - (input.buttons & Btn.Crouch ? 1 : 0);
  if (o.pitchClimb && input.moveZ > 0) vy += Math.sin(input.pitch) * input.moveZ;
  const l = Math.hypot(vx, vy, vz);
  if (l > 1) {
    vx /= l;
    vy /= l;
    vz /= l;
  }
  const sp = o.speed;
  const tx = vx * sp;
  const ty = vy * (o.vertical ?? sp);
  const tz = vz * sp;
  const a = Math.min(1, (o.accel ?? 40) * DT / Math.max(0.001, Math.hypot(tx - sim.c.Vel.x[eid]!, ty - sim.c.Vel.y[eid]!, tz - sim.c.Vel.z[eid]!)));
  sim.c.Move.kind[eid] = MoveKind.Flight;
  sim.c.Move.ticks[eid] = 2;
  sim.c.Move.x[eid] = sim.c.Vel.x[eid]! + (tx - sim.c.Vel.x[eid]!) * a;
  sim.c.Move.y[eid] = sim.c.Vel.y[eid]! + (ty - sim.c.Vel.y[eid]!) * a;
  sim.c.Move.z[eid] = sim.c.Vel.z[eid]! + (tz - sim.c.Vel.z[eid]!) * a;
  mods.gravityMul = 0;
  mods.canJump = false;
  return true;
}

/** Glide helper: caps fall speed and adds deterministic forward drift. */
export function glide(sim: Sim, eid: number, input: InputCmd, mods: MoveMods, o: { fallSpeed: number; driftSpeed?: number }) {
  if (sim.c.Player.grounded[eid]) return false;
  mods.gravityMul = 0.25;
  if (sim.c.Vel.y[eid]! < -Math.abs(o.fallSpeed)) sim.c.Vel.y[eid] = -Math.abs(o.fallSpeed);
  if (o.driftSpeed) {
    const d = wishDir(input);
    sim.c.Ext.x[eid] += d.x * o.driftSpeed * DT;
    sim.c.Ext.z[eid] += d.z * o.driftSpeed * DT;
  }
  return true;
}

/** General wall climb: while pushing into a wall, convert forward input into upward velocity. */
export function climb(sim: Sim, eid: number, input: InputCmd, mods: MoveMods, o: { speed: number; probe?: number }): boolean {
  const d = viewDir(input.yaw, 0);
  const p = add(sim.posOf(eid), { x: 0, y: sim.hero(eid).height * 0.5, z: 0 });
  if (input.moveZ <= 0.2 || sim.physics.raycast(p, d, o.probe ?? sim.hero(eid).radius + 0.35) < 0) return false;
  sim.c.Vel.y[eid] = o.speed;
  mods.gravityMul = 0;
  mods.canJump = false;
  return true;
}

/** Blink/teleport to an unobstructed point along aim; returns true if moved. */
export function blink(sim: Sim, eid: number, input: InputCmd, o: { range: number; up?: number; stopShort?: number }): boolean {
  const a = aim(sim, eid, o.range, false);
  const hit = sim.physics.raycast(a.origin, a.dir, o.range);
  const d = hit >= 0 ? Math.max(0, hit - (o.stopShort ?? sim.hero(eid).radius + 0.1)) : o.range;
  const p = add(a.origin, scale(a.dir, d));
  const feet = { x: p.x, y: p.y - sim.hero(eid).height * 0.5 + (o.up ?? 0), z: p.z };
  sim.c.Pos.x[eid] = feet.x;
  sim.c.Pos.y[eid] = feet.y;
  sim.c.Pos.z[eid] = feet.z;
  sim.c.Vel.x[eid] = sim.c.Vel.y[eid] = sim.c.Vel.z[eid] = 0;
  return true;
}

/** Starts/maintains a map-anchored swing; clear it when the supplied button is released. */
export function swing(sim: Sim, eid: number, input: InputCmd, heldButton: number, o: { range: number; seconds: number }): boolean {
  if (!(input.buttons & heldButton)) {
    if (sim.c.Move.kind[eid] === MoveKind.Swing) sim.c.Move.ticks[eid] = 0;
    return false;
  }
  if (sim.c.Move.kind[eid] === MoveKind.Swing && sim.c.Move.ticks[eid]! > 0) return true;
  const a = aim(sim, eid, o.range, false);
  const t = sim.physics.raycast(a.origin, a.dir, o.range);
  if (t < 0) return false;
  const anchor = add(a.origin, scale(a.dir, t));
  const center = add(sim.posOf(eid), { x: 0, y: sim.hero(eid).height * 0.5, z: 0 });
  startMove(sim, eid, MoveKind.Swing, secs(o.seconds), dist(anchor, center), anchor);
  return true;
}

/** Starts a dash toward a point or direction. */
export function dashTo(sim: Sim, eid: number, target: Vec3, o: { speed: number; seconds: number; kind?: MoveKind }) {
  const d = norm(sub(target, sim.posOf(eid)));
  startMove(sim, eid, o.kind ?? MoveKind.Dash, secs(o.seconds), o.speed, scale(d, o.speed));
}

/** Starts a leap toward a point using the shared homing scripted movement. */
export function leapTo(sim: Sim, eid: number, target: Vec3, o: { speed: number; seconds: number }) {
  startMove(sim, eid, MoveKind.Leap, secs(o.seconds), o.speed, target);
}

/** Starts a steerable charge in the current wish direction. */
export function charge(sim: Sim, eid: number, input: InputCmd, o: { speed: number; seconds: number }) {
  const d = wishDir(input);
  startMove(sim, eid, MoveKind.Charge, secs(o.seconds), o.speed, { x: d.x * o.speed, y: 0, z: d.z * o.speed });
}

/** Calls `onHit` once for each enemy touched by the mover's current body radius. */
export function contactEnemies(sim: Sim, eid: number, range: number, onHit: (target: number) => void) {
  for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), range, (e) => isEnemy(sim, eid, e), false)) onHit(t);
}

/** Upward knockback helper, respecting unstoppable through applyStatus for hard CC. */
export function knockUp(sim: Sim, target: number, speed: number) {
  if (sim.c.Status.unstoppable[target]! > 0) return;
  sim.c.Vel.y[target] = Math.max(sim.c.Vel.y[target]!, speed);
  sim.c.Player.grounded[target] = 0;
}

/** Pulls a target toward a point with shared scripted movement. */
export function pull(sim: Sim, target: number, point: Vec3, o: { speed: number; seconds: number; source?: number }) {
  if (sim.c.Status.unstoppable[target]! > 0) return;
  if (o.source !== undefined) applyStatus(sim, target, "root", o.seconds, { source: o.source });
  startMove(sim, target, MoveKind.Pulled, secs(o.seconds), o.speed, point);
}

/** Applies stealth and optionally breaks it when the caller reports an attack edge. */
export function stealth(sim: Sim, eid: number, seconds: number, o: { breakOnAttack?: boolean; attacked?: boolean; source?: number } = {}) {
  if (o.breakOnAttack && o.attacked) sim.c.Status.stealth[eid] = 0;
  else applyStatus(sim, eid, "stealth", seconds, { source: o.source });
}

/** Clears selected negative statuses; held releases through the shared callback path. */
export function cleanse(sim: Sim, target: number, statuses: StatusKind[] = ["stun", "root", "slow", "sleep", "knockdown", "antiHeal", "marked", "frozen", "held"]) {
  for (const s of statuses) {
    if (s === "held") releaseHeldTarget(sim, target);
    else sim.c.Status[s][target] = 0;
  }
}

/** Swaps to another registered hero while preserving team, position and optionally ult fraction. */
export function swapForm(sim: Sim, eid: number, hero: HeroId, o: { keepUlt?: boolean; hpFrac?: number } = {}) {
  const ult = sim.c.Ult.charge[eid]!;
  const pos = sim.posOf(eid);
  const team = sim.c.Player.team[eid]!;
  sim.setHero(eid, hero);
  sim.c.Player.team[eid] = team;
  sim.c.Pos.x[eid] = pos.x;
  sim.c.Pos.y[eid] = pos.y;
  sim.c.Pos.z[eid] = pos.z;
  if (o.keepUlt) sim.c.Ult.charge[eid] = ult;
  if (o.hpFrac !== undefined) {
    sim.c.Health.hp[eid] = sim.c.Health.maxHp[eid]! * o.hpFrac;
    sim.c.Health.armor[eid] = sim.c.Health.maxArmor[eid]! * o.hpFrac;
    sim.c.Health.shield[eid] = sim.c.Health.maxShield[eid]! * o.hpFrac;
  }
}

/** Copies an owner's yaw/pitch and action counters into another entity for clone/decoy visuals. */
export function mimicOwner(sim: Sim, owner: number, mimic: number) {
  sim.c.Look.yaw[mimic] = sim.c.Look.yaw[owner]!;
  sim.c.Look.pitch[mimic] = sim.c.Look.pitch[owner]!;
  sim.c.Weapon.action[mimic] = sim.c.Weapon.action[owner]!;
  sim.c.Weapon.actionTicks[mimic] = sim.c.Weapon.actionTicks[owner]!;
  sim.c.Weapon.shots[mimic] = sim.c.Weapon.shots[owner]!;
}

/** Where a construct fires from: `height` metres above its base. */
export const constructMuzzle = (sim: Sim, construct: number, height = 0.5): Vec3 => add(sim.posOf(construct), { x: 0, y: height, z: 0 });

/** Direction from a construct's muzzle to a point, e.g. its owner's aim point (`aim(sim, owner).point`). */
export const constructAimAt = (sim: Sim, construct: number, point: Vec3, height = 0.5): Vec3 => norm(sub(point, constructMuzzle(sim, construct, height)));

/** Construct-owned hitscan attributed to its owner for damage, KO credit and ult charge. */
export function constructHitscan(sim: Sim, construct: number, dir: Vec3, o: HitscanOpts & { height?: number }): HitResult {
  return hitscan(sim, sim.c.Construct.owner[construct]!, constructMuzzle(sim, construct, o.height), dir, o);
}

/** Construct-owned projectile attributed to its owner. */
export function constructProjectile(sim: Sim, construct: number, kind: number, speed: number, dir: Vec3, seconds: number, extra: { gravity?: number; radius?: number; height?: number } = {}) {
  const { height, ...rest } = extra;
  return spawnProjectile(sim, sim.c.Construct.owner[construct]!, { kind, origin: constructMuzzle(sim, construct, height), vel: scale(dir, speed), seconds, ...rest });
}

/** Periodic aura helper; amounts are per second and scaled by DT. */
export function aura(sim: Sim, owner: number, from: Vec3, o: { radius: number; healAlliesPerSec?: number; damageEnemiesPerSec?: number; los?: boolean }) {
  if (o.healAlliesPerSec)
    for (const { eid: t } of playersInRadius(sim, from, o.radius, (e) => !isEnemy(sim, owner, e), o.los ?? true)) applyHeal(sim, owner, t, o.healAlliesPerSec * DT);
  if (o.damageEnemiesPerSec)
    for (const { eid: t } of playersInRadius(sim, from, o.radius, (e) => isEnemy(sim, owner, e), o.los ?? true)) applyDamage(sim, t, o.damageEnemiesPerSec * DT, owner, { info: { kind: "aura" } });
}


/** Fuel-limited vertical thrust (hover / levitate / fly). Returns true while thrusting. */
export function jetpack(
  sim: Sim,
  eid: number,
  mods: MoveMods,
  fuelIdx: number,
  o: { want: boolean; drain: number; regenGround: number; regenAir: number; lift: number; maxUp: number },
): boolean {
  const c = sim.c;
  let fuel = hv(c, eid, fuelIdx);
  const on = o.want && fuel > 0 && c.Status.stun[eid]! <= 0;
  if (on) {
    fuel = Math.max(0, fuel - o.drain * DT);
    mods.lift = c.Vel.y[eid]! < o.maxUp ? GRAVITY + o.lift : GRAVITY;
    mods.canJump = false;
  } else fuel = Math.min(100, fuel + (c.Player.grounded[eid] ? o.regenGround : o.regenAir) * DT);
  setHv(c, eid, fuelIdx, fuel);
  return on;
}

export const airHold = (sim: Sim, eid: number, input: InputCmd, maxVy: number) =>
  !sim.c.Player.grounded[eid] && !!(input.buttons & Btn.Jump) && sim.c.Vel.y[eid]! < maxVy;

export function reviveAt(sim: Sim, o: number, pos: Vec3, by?: number) {
  const c = sim.c;
  const team = c.Player.team[o]!;
  sim.respawn(o);
  c.Player.team[o] = team;
  c.Pos.x[o] = pos.x;
  c.Pos.y[o] = pos.y;
  c.Pos.z[o] = pos.z;
  applyStatus(sim, o, "invuln", 1.5, { source: by });
  sim.emit({ type: "revive", target: c.Net.id[o]! });
}

/** Drops a point to the floor below it (for zones placed at the crosshair). */
export function toGround(sim: Sim, p: Vec3): Vec3 {
  const d = sim.physics.raycast(add(p, { x: 0, y: 0.5, z: 0 }), { x: 0, y: -1, z: 0 }, 30);
  return { x: p.x, y: p.y + 0.5 - (d >= 0 ? d : 0.5), z: p.z };
}

/** A grounded crosshair placement, backed away from wall hits by `clearance` metres. */
export function groundAimPoint(sim: Sim, eid: number, range: number, clearance = 0): Vec3 {
  const a = aim(sim, eid, range, false);
  let p = a.point;
  if (clearance > 0) {
    const hx = p.x - a.origin.x;
    const hz = p.z - a.origin.z;
    const l = Math.hypot(hx, hz);
    if (l > clearance) p = { x: p.x - (hx / l) * clearance, y: p.y, z: p.z - (hz / l) * clearance };
  }
  return toGround(sim, p);
}

/** A grounded point in front of the player, stopped short if a wall is immediately ahead. */
export function groundInFront(sim: Sim, eid: number, yaw: number, distance: number, clearance = 0): Vec3 {
  const { fwd } = flatBasis(yaw);
  const origin = add(sim.posOf(eid), { x: 0, y: sim.hero(eid).height * 0.5, z: 0 });
  const hit = sim.physics.raycast(origin, fwd, distance + clearance);
  const d = hit >= 0 ? Math.max(0, hit - clearance) : distance;
  return toGround(sim, add(sim.posOf(eid), { x: fwd.x * d, y: 1, z: fwd.z * d }));
}


var projectileRegistry: Record<number, ProjectileKind> = {};
export function setProjectileRegistry(r: Record<number, ProjectileKind>) {
  projectileRegistry = r;
}

export interface ProjectileKind {
  tick(sim: Sim, p: number): void;
  /** Continue through up to this many players/damageable constructs; map/barriers still stop it. */
  pierce?: number;
  /** Visual info for the client. */
  visual: { color: string; size: number; trail?: boolean };
}

export function simpleProjectile(
  onHit: (sim: Sim, p: number, owner: number, hit: ProjectileHit) => void,
  visual: ProjectileKind["visual"],
  opts: { mode?: HitMode; onExpire?: (sim: Sim, p: number, owner: number) => void } = {},
): ProjectileKind {
  // Pierce is read from this object (kits set `kind.pierce = n` after creating it), not
  // looked up through the registry by kind id.
  const kind: ProjectileKind = {
    visual,
    tick(sim, p) {
      const owner = sim.c.Projectile.owner[p]!;
      const hit = moveProjectile(sim, p, opts.mode);
      if (hit) {
        onHit(sim, p, owner, hit);
        const blockedConstruct = hit.construct !== undefined && !!(sim.c.Construct.flags[hit.construct]! & CONSTRUCT_FLAG_BLOCKS);
        const canPierce = !blockedConstruct && (hit.target !== undefined || hit.construct !== undefined) && (kind.pierce ?? 0) > sim.c.Projectile.bounces[p]!;
        if (canPierce) {
          sim.c.Projectile.bounces[p] = sim.c.Projectile.bounces[p]! + 1;
          return;
        }
        sim.destroyEntity(p);
        return;
      }
      if (sim.c.Projectile.ticks[p]! <= 0) {
        opts.onExpire?.(sim, p, owner);
        sim.destroyEntity(p);
      }
    },
  };
  return kind;
}

export const ownerAlive = (sim: Sim, owner: number) => sim.players.has(owner);
export const offWall = (hit: ProjectileHit, d: number) => (hit.normal ? add(hit.pos, scale(hit.normal, d)) : hit.pos);

export interface ZoneKind {
  tick(sim: Sim, z: number): void;
  end?(sim: Sim, z: number): void;
  visual: { color: string; dome: boolean };
}

export const alliesIn = (sim: Sim, z: number, lift = 0.5) => {
  const c = sim.c;
  const owner = c.Zone.owner[z]!;
  return playersInRadius(sim, add(sim.posOf(z), { x: 0, y: lift, z: 0 }), c.Zone.radius[z]!, (e) => !isEnemy(sim, owner, e), false).map((x) => x.eid);
};
